import * as React from 'react';

// eslint-disable-next-line
import once from 'lodash/once';
// eslint-disable-next-line
import isEmpty from 'lodash/isEmpty';

import { NotificationAction } from '@common/typescript/objects/NotificationAction';
import { Notification } from '@common/typescript/objects/Notification';
import { BaseUser } from '@common/react/objects/BaseUser';
import { AfterLogout, useApplicationContext } from '@common/react/components/Core/Application/Application';
import ItemProviderSynchronizer from '@common/react/components/Core/ItemProvider/ItemProviderSynchronizer';
import ItemProviderWithStore from '@common/react/components/Core/ItemProviderWithStore/ItemProviderWithStore';

import { CompanySettings, PrescriptionDeliveryType } from '@app/objects/Company';
import { User } from '@app/objects/User';

export interface CompanySettingsContext {
	companySettings: CompanySettings;
	checkCompanyErxAccess: (prescriptionDeliveryType: PrescriptionDeliveryType) => boolean;
	updateCompanySettings: (data: Partial<CompanySettings>) => void;
}

export const createCompanySettingsContext = once(() => React.createContext({} as CompanySettingsContext));

export const useCompanySettingsContext: () => CompanySettingsContext = () => {
	const context: CompanySettingsContext = React.useContext(createCompanySettingsContext());

	if (isEmpty(context)) throw 'Need CompanySettings context!';

	return context;
};

const storageName = 'companySettings';

const CompanySettingsSetter: React.FC = ({
	children,
}) => {
	const CompanySettingsContext = createCompanySettingsContext();
	const { getUser } = useApplicationContext();
	const user = getUser<User>();

	return (
		<>
			<ItemProviderWithStore<CompanySettings>
				storageName={storageName}
				id={-1}
				saveRequest="company"
				skipInitLoad
			>
				{({ actions, state: { item: companySettings } }) => {
					const updateCompanySettings = (item) => actions.setItem((prev) => ({ ...prev, ...item }));
					const checkCompanyErxAccess = (prescriptionDeliveryType: PrescriptionDeliveryType) =>
						companySettings.erxSettings.some((ce) => ce.prescriptionDeliveryType === prescriptionDeliveryType);
					const handle = (notification: Notification<BaseUser>, updateItem) => {
						const data = notification.data;
						if (notification.objectType === 'Init') {
							updateItem(data.companySettings);
						} else if (notification.objectType === 'company') {
							const inquiryCount = notification.data?.values?.newInquiryCounter;
							const reviewCount = notification.data?.values?.newReviewCounter;
							const pendingCounter = notification.data?.values?.pendingCounter;
							const rejectCounter = notification.data?.values?.rejectCounter;
							const faxCounter = notification.data?.values?.newFaxCounter;
							const mailCounter = notification.data?.values?.newEmailCounter;
							const analysisCounter = notification.data?.values?.newAnalysisCounter;
							const companyId = notification.data?.id;

							if (typeof (inquiryCount) !== 'undefined' && companyId === user?.companyId) {
								if (notification.action === NotificationAction.Add) {
									updateItem({
										newInquiryCount: inquiryCount,
									});
								} else {
									updateItem({
										newInquiryCount: companySettings.newInquiryCount + inquiryCount,
									});
								}
							}
							if (typeof (reviewCount) !== 'undefined' && companyId === user?.companyId) {
								if (notification.action === NotificationAction.Update) {
									updateItem({
										newReviewCount: companySettings.newReviewCount + reviewCount,
									});
								}
							}
							if (typeof (pendingCounter) !== 'undefined' && companyId) {
								if (notification.action === NotificationAction.Update) {
									updateItem({
										pendingCounter: companySettings.pendingCounter + pendingCounter,
									});
								}
							}
							if (typeof (rejectCounter) !== 'undefined' && companyId === user?.companyId) {
								if (notification.action === NotificationAction.Update) {
									updateItem({
										rejectCounter: companySettings.rejectCounter + rejectCounter,
									});
								}
							}
							if (typeof (faxCounter) !== 'undefined' && companyId === user?.companyId) {
								if (notification.action === NotificationAction.Update) {
									updateItem({
										newFaxCount: companySettings.newFaxCount + faxCounter,
									});
								}
							}
							if (typeof (mailCounter) !== 'undefined' && companyId === user?.companyId) {
								if (notification.action === NotificationAction.Update) {
									updateItem({
										newEmailCount: companySettings.newEmailCount + mailCounter,
									});
								}
							}
							if (typeof (analysisCounter) !== 'undefined' && companyId === user?.companyId) {
								if (notification.action === NotificationAction.Update) {
									updateItem({
										newAnalysisCount: companySettings.newAnalysisCount + analysisCounter,
									});
								}
							}
						} else if (notification.objectType === 'CompanyRedux' && notification?.data?.entity) {
							updateItem(notification.data.entity);
						}
					};
					return <>
						<ItemProviderSynchronizer
							objectType={['CompanyRedux', 'Init', 'company']}
							customHandler={(notification) => handle(notification, updateCompanySettings)}
						/>
						<CompanySettingsContext.Provider value={{ companySettings, checkCompanyErxAccess, updateCompanySettings }}>
							{children}
						</CompanySettingsContext.Provider>
						<AfterLogout callback={() => actions.setItem({} as CompanySettings)} />
					</>;
				}}
			</ItemProviderWithStore>
		</>
	);
};

export default CompanySettingsSetter;
